import axios from "axios";
import type {AssertNoSpecDrift, RequestSpecDrift, Schemas, SpecDrift} from "api/schemaAssert";
import type {CustomDomain} from "./customDomain/customDomain";

/**
 * The sender's bring-your-own custom transfer domain (kitecorp/transferify-api-service#348, with the edge
 * lifecycle from kitecorp/devops#126 phase B).
 *
 * <p>Separate from {@link AccountSubdomainApi} because these are different features that happen to share a
 * table and a plan capability: that one claims a label under OUR zone, this one adopts a host the sender
 * owns. Separate from {@link AccountBrandApi} because the gates differ per verb — registering and
 * verifying take the entitlement, while reading and REMOVING stay open on every tier so a downgraded owner
 * can still see and hand back the host they registered.
 *
 * <p>Verification and token rotation are POSTs rather than PUTs because neither is idempotent the way a
 * PUT promises: verification performs a live DNS lookup whose outcome depends on the world, and rotation
 * mints a new secret every time.
 */

/** POST `/account/domain` body. */
export interface CustomDomainRequest {
	domain: string;
}

export type _CustomDomain = AssertNoSpecDrift<SpecDrift<CustomDomain, Schemas["CustomDomainView"]>>;
export type _CustomDomainRequest =
	AssertNoSpecDrift<RequestSpecDrift<CustomDomainRequest, Schemas["CustomDomainRequest"]>>;

export class AccountDomainApi {
	/**
	 * The registered domain, or `null` when there is none — the api answers 204 with no body for that,
	 * which axios surfaces as an empty string rather than an object.
	 *
	 * <p>Called on every tier, deliberately. Entitlement says nothing about whether a domain exists, and a
	 * downgraded owner is indistinguishable from a never-entitled one until the api answers, so there is
	 * no narrower condition than "always" that cannot hide a registered host from the only person able to
	 * remove it.
	 */
	static async current(): Promise<CustomDomain | null> {
		const {status, data} = await axios.get<CustomDomain | "">("/account/domain");
		return status === 204 || data === "" ? null : (data as CustomDomain);
	}

	static async register(domain: string): Promise<CustomDomain> {
		const {data} = await axios.post<CustomDomain>("/account/domain", {domain} satisfies CustomDomainRequest);
		return data;
	}

	/**
	 * Proves ownership and, on success, provisions the host at the edge.
	 *
	 * <p>One call for both because the api folded provisioning into verification: it is the single retry
	 * affordance, so the same control that proves ownership also recovers a failed provision and there is
	 * never a second button to go looking for.
	 */
	static async verify(): Promise<CustomDomain> {
		const {data} = await axios.post<CustomDomain>("/account/domain/verification");
		return data;
	}

	/** Issues a fresh challenge token, invalidating the published value and returning the host to pending. */
	static async rotateVerificationToken(): Promise<CustomDomain> {
		const {data} = await axios.post<CustomDomain>("/account/domain/verification-token");
		return data;
	}

	static async remove(): Promise<void> {
		await axios.delete("/account/domain");
	}
}
